import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Loader2, Send, Sparkles, RefreshCw, History, Plus, Trash2, X, Key, FolderOpen, FileText, Check, Settings } from 'lucide-react';
import { predictAppearanceTraits, generateAppearanceSummary } from '../utils/appearancePredictor';
import { createRiskSummary } from '../utils/riskInterpreter';
import { useAuth } from '../contexts/AuthContext';
import APIKeyModal, { getStoredApiKey, STORAGE_KEY } from './APIKeyModal';
import { trackAiMessage, trackAiConversationStart } from '../analytics';
import MarkdownRenderer from './MarkdownRenderer';

// Cloudflare Worker proxy — forwards requests to api.anthropic.com with the
// user's API key, avoiding the CORS block on direct browser-to-Anthropic calls.
const AI_PROXY_URL = 'https://us-central1-geneie513.cloudfunctions.net/anthropicProxy';

const AIAssistant = ({ results }) => {
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKey, setApiKey] = useState(() => getStoredApiKey());

  // Listen for key changes from other tabs or manual updates
  useEffect(() => {
    const handler = () => setApiKey(getStoredApiKey());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  if (!apiKey) {
    return (
      <>
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 px-6 py-4">
            <div className="flex items-center gap-3 text-white">
              <div className="p-2.5 bg-white/20 rounded-xl">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold tracking-tight">AI Genetics Assistant</h3>
                <p className="text-xs text-indigo-200">Powered by Claude</p>
              </div>
            </div>
          </div>
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Key className="w-8 h-8 text-indigo-500" />
            </div>
            <h4 className="text-base font-bold text-slate-800 mb-2">API Key Required</h4>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              The AI assistant uses your own Anthropic API key — your key, your usage, your bill. GENEie never sees or stores it.
            </p>
            <button
              onClick={() => setShowApiKeyModal(true)}
              className="w-full py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white font-semibold rounded-xl transition-all shadow-md flex items-center justify-center gap-2 text-sm"
            >
              <Key className="w-4 h-4" />
              Enter API Key
            </button>
            <p className="mt-3 text-xs text-slate-400">
              Typical cost: $0.01-0.03 per conversation
            </p>
          </div>
        </div>
        {showApiKeyModal && (
          <APIKeyModal
            onClose={() => setShowApiKeyModal(false)}
            onKeySaved={(k) => setApiKey(k)}
          />
        )}
      </>
    );
  }

  return <AIAssistantFull results={results} apiKey={apiKey} onClearKey={() => setApiKey('')} />;
};

const AIAssistantFull = ({ results, apiKey, onClearKey }) => {
  const { currentUser } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [hasAutoAnalyzed, setHasAutoAnalyzed] = useState(false);
  const [chatHeight, setChatHeight] = useState(400);
  const [chatWidth, setChatWidth] = useState(420);
  const [isResizing, setIsResizing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [contextFiles, setContextFiles] = useState([]);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [savedFiles, setSavedFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingFileId, setLoadingFileId] = useState(null);
  const [retrievingVariants, setRetrievingVariants] = useState(false);
  const messagesContainerRef = useRef(null);
  const containerRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  useEffect(() => {
    const saved = localStorage.getItem('ai_conversations');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (!results) {
        // No genetic data loaded — clear any stale AI messages so they don't
        // show without the underlying data. Keep the conversation list intact
        // so history titles are preserved, just wipe the active messages.
        const cleared = parsed.map(c => ({ ...c, messages: [] }));
        setConversations(cleared);
        if (cleared.length > 0) setCurrentConversationId(cleared[0].id);
        setAiMessages([]);
      } else {
        setConversations(parsed);
        if (parsed.length > 0) {
          setCurrentConversationId(parsed[0].id);
          setAiMessages(parsed[0].messages);
        }
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  }, []);

  useEffect(() => {
    if (conversations.length > 0) {
      localStorage.setItem('ai_conversations', JSON.stringify(conversations));
    }
  }, [conversations]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    // Only scroll when a new message is added, not on initial load or loading state changes
    if (aiMessages.length > prevMessageCountRef.current) {
      scrollToBottom();
    }
    prevMessageCountRef.current = aiMessages.length;
  }, [aiMessages.length]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newHeight = e.clientY - rect.top - 140;
      if (newHeight >= 200 && newHeight <= 800) setChatHeight(newHeight);
      const newWidth = e.clientX - rect.left;
      if (newWidth >= 320 && newWidth <= 600) setChatWidth(newWidth);
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    if (isResizing) {
      document.body.style.cursor = 'nwse-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  useEffect(() => {
    if (!results || !results.snps || results.snps.length === 0) return;
    if (hasAutoAnalyzed) return;
    setHasAutoAnalyzed(true);

    // If the current conversation is empty, reuse it instead of creating a new one
    const current = conversations.find(c => c.id === currentConversationId);
    if (current && current.messages.length === 0) {
      performAutoAnalysis(currentConversationId);
    } else {
      createNewConversation(true);
    }
  }, [results, hasAutoAnalyzed]);

  const loadSavedFiles = async () => {
    if (!currentUser) return;
    setLoadingFiles(true);
    try {
      const { collection, query, where, getDocs, orderBy } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      const q = query(collection(db, 'geneticAnalyses'), where('userId', '==', currentUser.uid), orderBy('uploadedAt', 'desc'));
      const snap = await getDocs(q);
      setSavedFiles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Failed to load saved files:', err);
    }
    setLoadingFiles(false);
  };

  const loadFileAsContext = async (file) => {
    if (contextFiles.find(f => f.id === file.id)) {
      setContextFiles(prev => prev.filter(f => f.id !== file.id));
      return;
    }
    setLoadingFileId(file.id);
    try {
      const response = await fetch(file.analysisURL);
      if (!response.ok) throw new Error('Failed to fetch analysis');
      const data = await response.json();
      setContextFiles(prev => [...prev, {
        id: file.id,
        fileName: file.fileName,
        snps: data.snps || [],
        analyzedSnps: file.stats?.analyzedSnps || data.snps?.length || 0,
      }]);
    } catch (err) {
      console.error('Failed to load file context:', err);
      alert('Could not load ' + file.fileName + '. Please try again.');
    }
    setLoadingFileId(null);
  };

  const removeContextFile = (fileId) => setContextFiles(prev => prev.filter(f => f.id !== fileId));

  // ── Variant retrieval tool — searches all loaded SNP data locally ──────────
  const lookupVariants = ({ rsids = [], genes = [], limit = 25 }) => {
    const allSnps = [
      ...(results?.snps || []),
      ...contextFiles.flatMap(cf => cf.snps || []),
    ];

    const rsidSet = new Set((rsids || []).map(r => r.toLowerCase().trim()));
    const geneSet = new Set((genes || []).map(g => g.toLowerCase().trim()));

    const matches = allSnps.filter(snp => {
      if (rsidSet.size > 0 && snp.rsid && rsidSet.has(snp.rsid.toLowerCase())) return true;
      if (geneSet.size > 0 && snp.gene && geneSet.has(snp.gene.toLowerCase())) return true;
      return false;
    }).slice(0, Math.min(limit || 25, 25));

    if (matches.length === 0) {
      return { found: 0, message: 'No variants found matching those rsIDs or gene names.', variants: [] };
    }

    return {
      found: matches.length,
      variants: matches.map(snp => {
        const risk = createRiskSummary(snp);
        return {
          rsid: snp.rsid,
          gene: snp.gene,
          genotype: snp.genotype,
          chromosome: snp.chromosome,
          clinicalSignificance: snp.clinicalSignificance,
          riskCategory: risk.category,
          summary: risk.primaryMessage,
          conditions: snp.conditions,
          magnitude: snp.magnitude,
          repute: snp.repute,
          gwas: snp.gwas ? (Array.isArray(snp.gwas) ? snp.gwas.slice(0, 3).map(g => g.trait || g.study).join('; ') : String(snp.gwas)) : null,
        };
      }),
    };
  };

  // ── Slim context — just a summary, Claude retrieves specifics via tool ──────
  const buildSlimContext = (userMessage) => {
    const allSnps = [
      ...(results?.snps || []),
      ...contextFiles.flatMap(cf => cf.snps || []),
    ];

    const topClinical = allSnps
      .filter(snp => ['pathogenic', 'likely-pathogenic', 'risk'].includes(createRiskSummary(snp).category))
      .slice(0, 10)
      .map(snp => {
        const risk = createRiskSummary(snp);
        return '- ' + snp.rsid + ' (' + snp.gene + '): ' + risk.primaryMessage;
      });

    const filesSummary = [
      results ? 'Primary file: ' + (results.analyzedSnps || 0).toLocaleString() + ' SNPs, ' + (results.snps?.length || 0) + ' clinically relevant' : '',
      ...contextFiles.map(cf => 'Additional file "' + cf.fileName + '": ' + (cf.analyzedSnps || 0).toLocaleString() + ' SNPs'),
    ].filter(Boolean);

    const appearance = results?.snps
      ? 'Appearance: ' + generateAppearanceSummary(predictAppearanceTraits(results.snps))
      : '';

    const lines = [
      'GENETIC DATA SUMMARY:',
      ...filesSummary,
      appearance,
      '',
      'TOP CLINICAL FINDINGS (use lookup_variants for full details on any variant):',
      ...(topClinical.length > 0 ? topClinical : ['None flagged']),
      '',
      'You have access to the full dataset via the lookup_variants tool.',
      'Use it whenever the user asks about specific genes, rsIDs, conditions, or traits.',
      '',
      'User question: ' + userMessage,
    ];

    return lines.join('\n');
  };

  const LOOKUP_TOOL = {
    name: 'lookup_variants',
    description: 'Search the user genetic data for specific variants by rsID or gene name. Use this whenever you need details about a specific gene, rsID, or want to check if the user carries a particular variant. Returns up to 25 matches with full clinical data.',
    input_schema: {
      type: 'object',
      properties: {
        rsids: {
          type: 'array',
          items: { type: 'string' },
          description: 'List of rsIDs to look up, e.g. ["rs429358", "rs7412"]. Optional.',
        },
        genes: {
          type: 'array',
          items: { type: 'string' },
          description: 'List of gene names to look up, e.g. ["APOE", "BRCA1"]. Optional.',
        },
        limit: {
          type: 'number',
          description: 'Max variants to return (default 25, max 25).',
        },
      },
      required: [],
    },
  };

  const SYSTEM_PROMPT = [
    'You are Gene, a friendly genetics education assistant with access to the user full genetic dataset via the lookup_variants tool.',
    'When asked about specific genes, conditions, rsIDs, or traits, ALWAYS use lookup_variants first to get accurate data before answering.',
    'You can call it multiple times if needed.',
    'Provide accurate, evidence-based information.',
    'Use web_search for latest research when relevant.',
    'Always remind users to consult healthcare professionals.',
    'Be warm, encouraging, and explain concepts simply.',
  ].join(' ');

  const createNewConversation = async (autoAnalyze = false) => {
    if (autoAnalyze) {
      const emptyConv = conversations.find(c => c.messages.length === 0);
      if (emptyConv) {
        setCurrentConversationId(emptyConv.id);
        setAiMessages([]);
        trackAiConversationStart();
        await performAutoAnalysis(emptyConv.id);
        return;
      }
    }
    const newConvId = 'conv_' + Date.now();
    const newConv = {
      id: newConvId,
      title: autoAnalyze ? 'Initial Analysis' : 'New Chat',
      createdAt: new Date().toISOString(),
      messages: [],
    };
    setConversations(prev => [newConv, ...prev]);
    setCurrentConversationId(newConvId);
    setAiMessages([]);
    if (autoAnalyze) {
      trackAiConversationStart();
      await performAutoAnalysis(newConvId);
    }
  };

  const loadConversation = (convId) => {
    const conv = conversations.find(c => c.id === convId);
    if (conv) {
      setCurrentConversationId(convId);
      setAiMessages(conv.messages);
      setShowHistory(false);
    }
  };

  const deleteConversation = (convId, e) => {
    e.stopPropagation();
    if (window.confirm('Delete this conversation?')) {
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (currentConversationId === convId) {
        const remaining = conversations.filter(c => c.id !== convId);
        if (remaining.length > 0) loadConversation(remaining[0].id);
        else { setCurrentConversationId(null); setAiMessages([]); }
      }
    }
  };

  const updateCurrentConversation = (messages) => {
    if (!currentConversationId) return;
    setConversations(prev => prev.map(conv => {
      if (conv.id !== currentConversationId) return conv;
      let title = conv.title;
      if (title === 'New Chat' && messages.length > 0) {
        const first = messages.find(m => m.role === 'user');
        if (first) title = first.content.slice(0, 50) + (first.content.length > 50 ? '...' : '');
      }
      return { ...conv, title, messages, updatedAt: new Date().toISOString() };
    }));
  };

  const performAutoAnalysis = async (convId = currentConversationId) => {
    if (!results || !results.snps) return;
    setAiLoading(true);
    try {
      const appearanceSummary = generateAppearanceSummary(predictAppearanceTraits(results.snps));
      const topClinical = results.snps
        .filter(snp => ['pathogenic', 'likely-pathogenic', 'risk'].includes(createRiskSummary(snp).category))
        .slice(0, 5);

      const contextLines = [
        'I have analyzed the user genetic data.',
        '',
        'APPEARANCE TRAITS:',
        appearanceSummary,
        '',
        'TOP CLINICAL FINDINGS (' + topClinical.length + ' shown):',
        ...topClinical.map(snp => {
          const risk = createRiskSummary(snp);
          return '- ' + snp.rsid + ' (' + snp.gene + '): ' + risk.primaryMessage + (snp.conditions ? ' - ' + snp.conditions : '');
        }),
        '',
        'OVERALL STATS:',
        '- Total SNPs analyzed: ' + (results.analyzedSnps || 0).toLocaleString(),
        '- Clinically relevant variants: ' + results.snps.length,
        '',
        'Please provide a friendly, personalized welcome message that:',
        '1. Starts with their appearance predictions',
        '2. Mentions 2-3 significant clinical findings encouragingly',
        '3. Reminds them this is educational, not diagnostic',
        '4. Invites them to ask questions',
        'Keep it warm, conversational, under 150 words.',
      ];

      const response = await fetch(AI_PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 500,
          system: 'You are Gene, a friendly genetics education assistant. Be warm, encouraging, and educational. Never provide medical advice.',
          messages: [{ role: 'user', content: contextLines.join('\n') }],
        }),
      });

      const data = await response.json();
      const assistantMessage = data.content.map(item => item.type === 'text' ? item.text : '').join('');
      const newMessages = [{ role: 'assistant', content: assistantMessage }];
      setAiMessages(newMessages);
      updateCurrentConversation(newMessages);
    } catch (error) {
      console.error('Auto-analysis error:', error);
      const fallback = [{ role: 'assistant', content: 'Hi! I have analyzed your genetic data and found ' + results.snps.length + ' clinically relevant variants. Ask me anything about your results!' }];
      setAiMessages(fallback);
      updateCurrentConversation(fallback);
    }
    setAiLoading(false);
  };

  const sendAiMessage = async () => {
    if (!aiInput.trim() || aiLoading) return;
    const userMessage = aiInput.trim();
    setAiInput('');

    // Cap history at last 10 messages to control token costs
    const recentHistory = aiMessages.slice(-10);
    const newMessages = [...aiMessages, { role: 'user', content: userMessage }];
    setAiMessages(newMessages);
    updateCurrentConversation(newMessages);
    setAiLoading(true);

    try {
      let apiMessages = [
        ...recentHistory.map(m => ({ role: m.role, content: m.content })),
        { role: 'user', content: buildSlimContext(userMessage) },
      ];

      let finalText = '';
      let loopCount = 0;
      const MAX_LOOPS = 5;

      while (loopCount < MAX_LOOPS) {
        loopCount++;

        const response = await fetch(AI_PROXY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
          body: JSON.stringify({
            model: 'claude-sonnet-4-6',
            max_tokens: 1500,
            system: SYSTEM_PROMPT,
            tools: [
              LOOKUP_TOOL,
              { type: 'web_search_20250305', name: 'web_search' },
            ],
            messages: apiMessages,
          }),
        });

        const data = await response.json();
        if (data.error) throw new Error(data.error.message);

        const textBlocks = data.content.filter(b => b.type === 'text');
        if (textBlocks.length > 0) {
          finalText = textBlocks.map(b => b.text).join('');
        }

        const toolUseBlocks = data.content.filter(b => b.type === 'tool_use');

        if (data.stop_reason === 'end_turn' || toolUseBlocks.length === 0) {
          break;
        }

        setRetrievingVariants(true);

        const toolResults = toolUseBlocks.map(toolUse => {
          let result;
          if (toolUse.name === 'lookup_variants') {
            result = lookupVariants(toolUse.input);
          } else {
            result = { error: 'Unknown tool: ' + toolUse.name };
          }
          return {
            type: 'tool_result',
            tool_use_id: toolUse.id,
            content: JSON.stringify(result),
          };
        });

        apiMessages = [
          ...apiMessages,
          { role: 'assistant', content: data.content },
          { role: 'user', content: toolResults },
        ];
      }

      setRetrievingVariants(false);
      trackAiMessage({
        usedLookup: loopCount > 1,
        usedWebSearch: false,
        contextFileCount: contextFiles.length,
      });

      const assistantMessage = finalText || 'I looked up your variants but had trouble forming a response. Please try again.';
      const updatedMessages = [...newMessages, { role: 'assistant', content: assistantMessage }];
      setAiMessages(updatedMessages);
      updateCurrentConversation(updatedMessages);
    } catch (error) {
      setRetrievingVariants(false);
      console.error('AI Error:', error);
      const errorMessages = [...newMessages, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }];
      setAiMessages(errorMessages);
      updateCurrentConversation(errorMessages);
    }
    setAiLoading(false);
  };

  const getCurrentConversation = () => conversations.find(c => c.id === currentConversationId);

  return (
    <div ref={containerRef} className="bg-white rounded-2xl shadow-xl overflow-hidden sticky top-8 border border-slate-200 relative" style={{ width: chatWidth + 'px' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 px-6 py-4 border-b border-indigo-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold tracking-tight">AI Genetics Assistant</h3>
              <p className="text-xs text-indigo-200">Powered by Claude</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => createNewConversation(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors" title="New chat">
              <Plus className="w-4 h-4 text-white" />
            </button>
            <button onClick={onClearKey} className="p-2 hover:bg-white/20 rounded-lg transition-colors" title="Change API key">
              <Key className="w-4 h-4 text-white" />
            </button>
            {aiMessages.length > 0 && (
              <button onClick={() => createNewConversation(true)} className="p-2 hover:bg-white/20 rounded-lg transition-colors" title="Restart">
                <RefreshCw className="w-4 h-4 text-white" />
              </button>
            )}
          </div>
        </div>
        {getCurrentConversation() && (
          <div className="mt-2 pt-2 border-t border-indigo-500/30">
            <p className="text-xs text-indigo-200 truncate">{getCurrentConversation().title}</p>
          </div>
        )}
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-slate-50 to-white" style={{ height: chatHeight + 'px' }}>
        {aiMessages.length === 0 && !aiLoading && !results && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-indigo-600" />
            </div>
            <h4 className="text-base font-semibold text-slate-800 mb-2">Upload Your Genetic Data</h4>
            <p className="text-sm text-slate-600 mb-3 max-w-xs mx-auto">I will automatically analyze your appearance traits, health variants, and answer your questions.</p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-full">
              <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
              <span className="text-xs font-medium text-indigo-700">AI-Powered Analysis</span>
            </div>
          </div>
        )}
        {aiMessages.length === 0 && aiLoading && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
            <div className="text-center">
              <p className="text-sm font-medium text-slate-700 mb-1">Analyzing your genetics...</p>
              <p className="text-xs text-slate-500">Examining appearance traits and health variants</p>
            </div>
          </div>
        )}
        {aiMessages.map((msg, idx) => (
          <div
            key={idx}
            className={
              (msg.role === 'user'
                ? 'ml-auto bg-gradient-to-br from-indigo-600 to-indigo-700 text-white shadow-md'
                : 'mr-auto bg-white border border-slate-200 text-slate-800 shadow-sm') +
              ' max-w-[85%] px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed'
            }
          >
            {msg.role === 'assistant' && (
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100">
                <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Gene AI</span>
              </div>
            )}
            <div className={msg.role === 'assistant' ? 'text-slate-700' : ''}>
                {msg.role === 'assistant'
                  ? <MarkdownRenderer content={msg.content} />
                  : msg.content}
              </div>
          </div>
        ))}
        {aiLoading && aiMessages.length > 0 && (
          <div className="flex items-center gap-2.5 mr-auto bg-white border border-slate-200 px-4 py-3 rounded-2xl shadow-sm max-w-[85%]">
            <Loader2 className="w-4 h-4 animate-spin text-indigo-600 flex-shrink-0" />
            <span className="text-sm text-slate-600">
              {retrievingVariants ? 'Looking up your variants...' : 'Thinking...'}
            </span>
          </div>
        )}
      </div>

      {/* Resize handle */}
      <div onMouseDown={() => setIsResizing(true)} className="absolute right-0 bottom-0 w-6 h-6 cursor-nwse-resize z-10">
        <div className="w-full h-full bg-slate-300 hover:bg-indigo-500 transition-colors" style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)', borderBottomRightRadius: '1rem' }} />
      </div>

      {/* File picker panel */}
      {showFilePicker && (
        <div className="border-t border-slate-200 bg-white">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-indigo-600" />
              Add File Context
            </h4>
            <button onClick={() => setShowFilePicker(false)} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto p-2">
            {loadingFiles ? (
              <div className="flex items-center justify-center py-6 gap-2 text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs">Loading your files...</span>
              </div>
            ) : savedFiles.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-400">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No saved files found. Upload and save a file to use multi-file analysis.
              </div>
            ) : (
              savedFiles.map(file => {
                const isLoaded = !!contextFiles.find(f => f.id === file.id);
                const isLoadingThis = loadingFileId === file.id;
                return (
                  <div
                    key={file.id}
                    onClick={() => !isLoadingThis && loadFileAsContext(file)}
                    className={
                      'flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors mb-1 border ' +
                      (isLoaded ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:bg-slate-50')
                    }
                  >
                    <div className={'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ' + (isLoaded ? 'bg-indigo-100' : 'bg-slate-100')}>
                      {isLoadingThis
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />
                        : isLoaded
                        ? <Check className="w-3.5 h-3.5 text-indigo-600" />
                        : <FileText className="w-3.5 h-3.5 text-slate-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 truncate">{file.fileName}</p>
                      <p className="text-[10px] text-slate-500">
                        {file.stats?.analyzedSnps?.toLocaleString()} variants · {new Date(file.uploadedAt?.toDate?.() || file.uploadedAt).toLocaleDateString()}
                      </p>
                    </div>
                    {isLoaded && <span className="text-[10px] font-semibold text-indigo-600 flex-shrink-0">Added</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 bg-white border-t border-slate-200">
        {contextFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider self-center">Context:</span>
            {contextFiles.map(cf => (
              <div key={cf.id} className="flex items-center gap-1 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded-full">
                <FileText className="w-3 h-3 text-indigo-500" />
                <span className="text-[11px] font-medium text-indigo-700 max-w-[120px] truncate">{cf.fileName}</span>
                <button onClick={() => removeContextFile(cf.id)} className="ml-0.5 hover:text-red-500 text-indigo-400 transition-colors">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2.5">
          <button
            onClick={() => { setShowFilePicker(v => !v); setShowHistory(false); if (!showFilePicker) loadSavedFiles(); }}
            title="Add file context"
            className={
              'px-3 py-2.5 rounded-xl border transition-all flex items-center gap-1.5 flex-shrink-0 ' +
              (contextFiles.length > 0 || showFilePicker
                ? 'bg-indigo-50 border-indigo-300 text-indigo-600'
                : 'bg-slate-50 border-slate-300 text-slate-500 hover:border-indigo-300 hover:text-indigo-600')
            }
          >
            <FolderOpen className="w-4 h-4" />
            {contextFiles.length > 0 && <span className="text-xs font-bold">{contextFiles.length}</span>}
          </button>
          <input
            type="text"
            value={aiInput}
            onChange={(e) => setAiInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendAiMessage()}
            placeholder={results ? 'Ask about your genetics...' : 'Upload data first...'}
            disabled={!results}
            className="flex-1 bg-slate-50 border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          />
          <button
            onClick={sendAiMessage}
            disabled={aiLoading || !aiInput.trim() || !results}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all flex items-center gap-2 shadow-sm"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <p className="text-xs text-slate-500 flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full" />
            Your data stays private · Educational use only
          </p>
          <button
            onClick={() => { setShowHistory(!showHistory); setShowFilePicker(false); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <History className="w-3.5 h-3.5" />
            <span>History</span>
            {conversations.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[10px] font-bold">{conversations.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* History */}
      {showHistory && (
        <div className="border-t border-slate-200 bg-slate-50 max-h-64 overflow-y-auto">
          <div className="p-3 border-b border-slate-200 flex items-center justify-between bg-white sticky top-0">
            <h4 className="text-sm font-semibold text-slate-800">Chat History</h4>
            <button onClick={() => setShowHistory(false)} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
              <X className="w-4 h-4 text-slate-600" />
            </button>
          </div>
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-xs">No conversations yet</p>
            </div>
          ) : (
            <div className="p-2">
              {conversations.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => loadConversation(conv.id)}
                  className={
                    'p-2.5 rounded-lg cursor-pointer transition-colors group mb-2 border ' +
                    (currentConversationId === conv.id ? 'bg-indigo-100 border-indigo-300' : 'bg-white hover:bg-slate-100 border-slate-200')
                  }
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 truncate">{conv.title}</p>
                      <p className="text-[10px] text-slate-500 mt-1">
                        {new Date(conv.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {' · '}{conv.messages.length} msgs
                      </p>
                    </div>
                    <button onClick={(e) => deleteConversation(conv.id, e)} className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded transition-all">
                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIAssistant;